import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from './client'
import { queryKeys } from '../lib/query'
import type { Project, ProjectStage, KanbanData, Payment } from '@1130-studio/shared'

// Types for API responses (apiClient already unwraps the { success, data } envelope)
interface PaginatedProjectsResponse {
  items: Project[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}
type KanbanResponse = KanbanData
type ProjectResponse = Project & { payments?: Payment[] }
type StagesResponse = ProjectStage[]

interface CreateProjectData {
  name: string
  description?: string
  clientId?: string | null
  leadId?: string | null
  stageId?: string | null
  priority?: 'low' | 'medium' | 'high' | 'urgent'
  budget?: string | null
  startDate?: string | null
  deadline?: string | null
}

interface MoveProjectData {
  stageId: string
  position: number
}

// Fetch projects in Kanban format
export function useKanban() {
  return useQuery({
    queryKey: queryKeys.projects.kanban,
    queryFn: () => apiClient<KanbanResponse>('/projects?format=kanban'),
  })
}

// Fetch projects as list (paginated)
export function useProjects(filters?: { search?: string; stageId?: string; priority?: string; clientId?: string }) {
  return useQuery({
    queryKey: queryKeys.projects.all,
    queryFn: () => {
      const params = new URLSearchParams()
      if (filters?.search) params.set('search', filters.search)
      if (filters?.stageId) params.set('stageId', filters.stageId)
      if (filters?.priority) params.set('priority', filters.priority)
      if (filters?.clientId) params.set('clientId', filters.clientId)

      const queryString = params.toString()
      const url = `/projects${queryString ? `?${queryString}` : ''}`
      return apiClient<PaginatedProjectsResponse>(url)
    },
  })
}

// Fetch all projects (unpaginated, for dropdowns/selects)
export function useProjectsList() {
  return useQuery({
    queryKey: [...queryKeys.projects.all, 'list'],
    queryFn: async () => {
      const data = await apiClient<PaginatedProjectsResponse>('/projects?pageSize=100')
      return data.items
    },
  })
}

// Fetch single project
export function useProject(id: string) {
  return useQuery({
    queryKey: queryKeys.projects.detail(id),
    queryFn: () => apiClient<ProjectResponse>(`/projects/${id}`),
    enabled: !!id,
  })
}

// Fetch stages
export function useStages() {
  return useQuery({
    queryKey: queryKeys.stages.all,
    queryFn: () => apiClient<StagesResponse>('/stages'),
  })
}

// Reorder stages mutation
export function useReorderStages() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (stages: { id: string; position: number }[]) =>
      apiClient<StagesResponse>('/stages/reorder', {
        method: 'POST',
        body: JSON.stringify({ stages }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.stages.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.pipeline.all })
    },
  })
}

// Create project mutation
export function useCreateProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateProjectData) =>
      apiClient<ProjectResponse>('/projects', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.kanban })
    },
  })
}

// Update project mutation
export function useUpdateProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateProjectData> }) =>
      apiClient<ProjectResponse>(`/projects/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.kanban })
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.detail(data.id) })
    },
  })
}

// Delete project mutation
export function useDeleteProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient(`/projects/${id}`, { method: 'DELETE' })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.kanban })
    },
  })
}

// Move project (drag & drop) mutation
export function useMoveProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: MoveProjectData }) =>
      apiClient<ProjectResponse>(`/projects/${id}/move`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onMutate: async ({ id, data }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.projects.kanban })

      // Snapshot the previous value
      const previousKanban = queryClient.getQueryData<KanbanData>(queryKeys.projects.kanban)

      // Optimistically update the kanban data
      if (previousKanban) {
        const newKanban = { ...previousKanban }
        const newProjects = { ...newKanban.projects }

        // Find and remove project from its current stage
        let movedProject: Project | undefined
        for (const stageId in newProjects) {
          const index = newProjects[stageId].findIndex((p) => p.id === id)
          if (index !== -1) {
            movedProject = newProjects[stageId][index]
            newProjects[stageId] = [
              ...newProjects[stageId].slice(0, index),
              ...newProjects[stageId].slice(index + 1),
            ]
            break
          }
        }

        // Add project to new stage at new position
        if (movedProject) {
          if (!newProjects[data.stageId]) {
            newProjects[data.stageId] = []
          }
          const updatedProject = { ...movedProject, stageId: data.stageId, position: data.position }
          newProjects[data.stageId] = [
            ...newProjects[data.stageId].slice(0, data.position),
            updatedProject,
            ...newProjects[data.stageId].slice(data.position),
          ]
        }

        newKanban.projects = newProjects
        queryClient.setQueryData(queryKeys.projects.kanban, newKanban)
      }

      return { previousKanban }
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousKanban) {
        queryClient.setQueryData(queryKeys.projects.kanban, context.previousKanban)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.kanban })
    },
  })
}
